import React, { CSSProperties, forwardRef, MouseEvent, useCallback } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { stopEvents } from "@mapmycustomers/shared/util/browser";

import EntityChip from "@app/component/EntityChip";
import Lexer from "@app/component/Lexer";
import MultilineText from "@app/component/typography/MultilineText";
import { showEntityView } from "@app/store/entityView/actions";
import EntityViewPayload from "@app/store/entityView/EntityViewPayload";
import { getAllTeamsOfEntireOrganization, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { TimelineEventNoteAdded } from "@app/types/events/TimelineEvent";

import BaseEvent from "./BaseEvent";
import styles from "./RecordEvent.module.scss";

export interface RecordEventProps {
  event: TimelineEventNoteAdded;
  expanded: boolean;
  onSizeChange?: () => void;
  style?: CSSProperties;
}

interface Props extends RecordEventProps {
  onShowEntityView: (payload: EntityViewPayload) => void;
  teams: Team[];
  users: User[];
}

const NoteAddedEvent = forwardRef<HTMLDivElement, Props>(
  ({ event, expanded, onShowEntityView, onSizeChange, style, teams, users }, ref) => {
    const intl = useIntl();

    const record = event.entity;

    const handleShowEntity = useCallback(
      (payload: EntityViewPayload, e?: MouseEvent<HTMLElement>) => {
        stopEvents(e);
        onShowEntityView(payload);
      },
      [onShowEntityView]
    );

    return (
      <BaseEvent
        date={event.updatedAt}
        expanded={expanded}
        onSizeChange={onSizeChange}
        ref={ref}
        style={style}
        title={intl.formatMessage(
          {
            id: "dashboard.timeline.event.noteAdded",
            defaultMessage: "logged a note on {record}",
            description: "Title of the New Note event item",
          },
          {
            record: (
              <EntityChip
                entityId={record.id}
                entityType={record.entity}
                inline
                name={record.name}
                onClick={handleShowEntity}
              />
            ),
          }
        )}
        user={event.updatedBy}
      >
        <div className={styles.noteContainer}>
          <MultilineText wrapLinesWith={Lexer} wrapperProps={{ teams, users }}>
            {event.note.note}
          </MultilineText>
        </div>
      </BaseEvent>
    );
  }
);

const mapStateToProps = (state: RootState) => ({
  teams: getAllTeamsOfEntireOrganization(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  onShowEntityView: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(
  NoteAddedEvent
);
